import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import type { CashflowSummary, Currency } from "../../types";
import {
  formatCurrency,
  formatK,
} from "../cashflow/CashflowContext";
import {
  formatYmMonthChartLabel,
  formatYmMonthShort,
} from "../../utils/formatYmMonthShort";

type ScenarioPoint = {
  month: string;
  actual?: number | null;
  baseline: number;
  expectedIncome: number;
  expenses: number;
  isForecast: boolean;
};

type Props = {
  points: ScenarioPoint[];
  todayMonth?: string;
  currency: Currency;
  cashflowSummary?: CashflowSummary;
  cashflowSummaryLoading: boolean;
  includeFutureInvoices: boolean;
  onToggleFutureInvoices: (next: boolean) => void;
  isLoading: boolean;
};

const BURN_BAR_FILL = "#a78bfa";

export function DashboardDebtAndScenario({
  points,
  todayMonth,
  currency,
  cashflowSummary,
  cashflowSummaryLoading,
  includeFutureInvoices,
  onToggleFutureInvoices,
  isLoading,
}: Props) {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const burnChartContainerRef = useRef<HTMLDivElement | null>(null);
  const [canRenderChart, setCanRenderChart] = useState(false);
  const [canRenderBurnChart, setCanRenderBurnChart] = useState(false);

  useEffect(() => {
    const element = chartContainerRef.current;
    if (!element) {
      setCanRenderChart(false);
      return;
    }

    const updateSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setCanRenderChart(width > 0 && height > 0);
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isLoading, points.length]);

  useEffect(() => {
    const element = burnChartContainerRef.current;
    if (!element) {
      setCanRenderBurnChart(false);
      return;
    }

    const updateSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setCanRenderBurnChart(width > 0 && height > 0);
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [cashflowSummaryLoading, cashflowSummary?.periods?.length ?? 0]);

  const trendData = (points ?? []).map((p) => ({
    ...p,
    monthLabel: formatYmMonthShort(p.month),
  }));

  const burnChartData = (cashflowSummary?.periods ?? []).map((p) => ({
    monthLabel: formatYmMonthChartLabel(p.month),
    burn: Math.max(0, p.expenses),
  }));

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">
          Monthly Cash Burn
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Cash out per calendar month in your dashboard range.
        </p>
        {cashflowSummaryLoading ? (
          <div className="mt-4 h-[260px] animate-pulse rounded-lg bg-slate-100" />
        ) : burnChartData.length === 0 ? (
          <div className="mt-4 flex h-[260px] items-center justify-center rounded-lg bg-slate-50 text-sm text-slate-500">
            No data for this range
          </div>
        ) : (
          <div
            ref={burnChartContainerRef}
            className="mt-4 h-[260px] min-h-[260px] w-full min-w-0"
          >
            {canRenderBurnChart ? (
              <ResponsiveContainer
                width="100%"
                height="100%"
                minWidth={0}
                minHeight={260}
              >
                <BarChart
                  data={burnChartData}
                  margin={{ top: 8, right: 8, bottom: 4, left: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="monthLabel"
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    interval={0}
                  />
                  <YAxis
                    tickFormatter={(v) => formatK(Number(v), currency)}
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <Tooltip
                    formatter={(v: number | string) =>
                      formatCurrency(Number(v), currency)
                    }
                    labelFormatter={(label) => String(label)}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                    }}
                  />
                  <Bar
                    dataKey="burn"
                    name="Cash burn"
                    fill={BURN_BAR_FILL}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full w-full animate-pulse rounded-lg bg-slate-100" />
            )}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-slate-900">
            Scenario Planner (Forecast)
          </h3>
          <div className="flex items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600">
              <button
                type="button"
                role="switch"
                aria-checked={includeFutureInvoices}
                onClick={() => onToggleFutureInvoices(!includeFutureInvoices)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  includeFutureInvoices ? "bg-blue-600" : "bg-slate-300"
                }`}
                aria-label="Include future invoices"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    includeFutureInvoices ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </button>
              Include future invoices
            </label>
            <Link
              to="/cashflow"
              className="text-sm font-medium text-blue-500 hover:text-slate-900 hover:underline"
            >
              Open Planner
            </Link>
          </div>
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Actual net position and forecast scenarios from your saved planner
          values.
        </p>
        {isLoading ? (
          <div className="h-[240px] animate-pulse rounded-lg bg-slate-100" />
        ) : trendData.length === 0 ? (
          <div className="flex h-[240px] items-center justify-center rounded-lg bg-slate-50 text-sm text-slate-500">
            No data for this range
          </div>
        ) : (
          <div
            ref={chartContainerRef}
            className="h-[260px] min-h-[260px] w-full min-w-0"
          >
            {canRenderChart ? (
              <ResponsiveContainer
                width="100%"
                height="100%"
                minWidth={0}
                minHeight={260}
              >
                <LineChart
                  data={trendData}
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="monthLabel"
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <YAxis
                    tickFormatter={(v) => formatK(Number(v), currency)}
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <Tooltip
                    formatter={(v: number | string) =>
                      formatK(Number(v), currency)
                    }
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    name="Actual"
                    stroke="#111827"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="expectedIncome"
                    name="Expected income"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="4 4"
                  />
                  <Line
                    type="monotone"
                    dataKey="baseline"
                    name="Remaining"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="expenses"
                    name="Expenses"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="4 4"
                  />
                  {todayMonth ? (
                    <ReferenceLine
                      x={formatYmMonthShort(todayMonth)}
                      stroke="#334155"
                      strokeDasharray="3 5"
                      strokeWidth={1.5}
                    />
                  ) : null}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full w-full animate-pulse rounded-lg bg-slate-100" />
            )}
          </div>
        )}
        <div className="mt-3 flex flex-wrap justify-center gap-4 text-xs">
          <span className="inline-flex items-center gap-2 text-slate-800">
            <span className="h-3.5 w-3.5 rounded-full bg-slate-900" />
            Actual
          </span>
          <span className="inline-flex items-center gap-2 text-green-600">
            <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
            Cash In
          </span>
          <span className="inline-flex items-center gap-2 text-blue-600">
            <span className="h-3.5 w-3.5 rounded-full bg-blue-500" />
            Net
          </span>
          <span className="inline-flex items-center gap-2 text-red-500">
            <span className="h-3.5 w-3.5 rounded-full bg-red-500" />
            Cash Out
          </span>
        </div>
      </section>
    </div>
  );
}
